// re.rejouis.fr — recherche catalogue réutilisable (home + écran Catalogue).
// Composant CONTRÔLÉ : la valeur `q` est portée par le parent (permet de pré-remplir
// la recherche, ex. depuis une campagne). Affiche le champ + (si recherche) les
// résultats et le repli (lot / don, ou demande de référencement si activée).
import { useEffect, useRef, useState } from 'react';
import { Icons, Illus, Spinner } from './ui.jsx';
import CarteProduit from './CarteProduit.jsx';
import { rechercheCatalogue, produitsParEan } from '../lib/api.js';
import { scanEan } from '../lib/eanScanner.js';
import { REFERENCEMENT_ACTIF } from '../lib/flags.js';
import { suivre } from '../lib/posthog.js';

// `limite` : n'affiche que les N premiers résultats + « Voir les X résultats » (home),
// pour laisser visibles les autres façons de revendre en dessous.
// `hero` : champ plus grand et plus marqué (home).
// `onValider` : touche Entrée (home → page de recherche dédiée). Sans elle, Entrée
// ferme juste le clavier mobile.
export default function RechercheModele({ go, panier, onChoisir, q, setQ, autoFocus, limite, hero, onValider }) {
  const [etat, setEtat] = useState('idle'); // idle | loading | ok | error
  const [produits, setProduits] = useState([]);
  const [erreur, setErreur] = useState(null);
  const inputRef = useRef(null);
  const idsPanier = new Set(panier.map((l) => l.produitId));
  const enRecherche = q.trim().length >= 2;
  // Scan du code-barres de la boîte : résultat affiché à la place de la recherche texte,
  // jusqu'à la prochaine frappe. { etat: loading|ok|error, ean, trouves, produits }
  const [ean, setEan] = useState(null);

  async function scannerCodeBarres() {
    const code = await scanEan({ titre: 'Scanne le code-barres de la boîte' });
    if (!code) return;
    setQ('');
    setEan({ etat: 'loading', ean: code, produits: [] });
    try {
      const r = await produitsParEan(code);
      suivre('barcode_scan_completed', { match_count: r.count || 0, known: (r.trouves || 0) > 0 });
      setEan({ etat: 'ok', ean: code, trouves: r.trouves || 0, produits: r.produits || [] });
    } catch (e) {
      setEan({ etat: 'error', ean: code, produits: [] });
    }
  }

  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);

  // Recherche débouncée.
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setEtat('idle'); setProduits([]); return; }
    setEtat('loading');
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await rechercheCatalogue(s);
        setProduits(r.produits || []);
        setEtat('ok');
      } catch (e) {
        setErreur(e.message);
        setEtat('error');
      }
    }, 320);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  // Recherche « aboutie » : la frappe s'est arrêtée 1,5 s sur des résultats affichés
  // (on ne trace pas chaque lettre). result_count = 0 → modèle cherché mais absent.
  useEffect(() => {
    if (etat !== 'ok') return;
    const s = q.trim().toLowerCase();
    const t = setTimeout(() => suivre('catalog_search', { query: s, result_count: produits.length }), 1500);
    return () => clearTimeout(t);
  }, [etat, produits, q]);

  return (
    <div>
      <div style={{ padding: '2px 16px 4px' }}>
        <form role="search" style={{ position: 'relative' }} onSubmit={(e) => {
          e.preventDefault();
          if (q.trim().length >= 2) {
            const queryLength = q.trim().length;
            suivre('catalog_search_submitted', { query: q.trim().toLowerCase(), query_length: queryLength });
          }
          if (onValider && q.trim().length >= 2) onValider(q.trim());
          else inputRef.current?.blur();
        }}>
          <Icons.search size={hero ? 21 : 18} style={{ position: 'absolute', left: hero ? 18 : 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--stone)' }} />
          <input ref={inputRef} enterKeyHint="search" className={'search-input' + (hero ? ' hero' : '')} style={{ paddingRight: hero ? 62 : 54 }}
            placeholder={hero ? 'Marque ou modèle, ex. Womanizer' : 'Marque ou modèle…'} value={q}
            onChange={(e) => { setEan(null); setQ(e.target.value); }} />
          <button type="button" onClick={scannerCodeBarres} aria-label="Scanner le code-barres de la boîte" title="Scanner le code-barres de la boîte"
            style={{ position: 'absolute', right: hero ? 8 : 6, top: '50%', transform: 'translateY(-50%)', width: hero ? 44 : 38, height: hero ? 44 : 38,
              borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bleu-pale)', color: 'var(--bleu)' }}>
            <Icons.barcode size={hero ? 22 : 19} />
          </button>
        </form>
      </div>

      {ean && !enRecherche ? (
        <div style={{ padding: '0 16px' }}>
          <div className="t-overline" style={{ padding: '16px 0 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
            {ean.etat === 'loading' ? <Spinner label="Recherche du code-barres…" /> : <>Code-barres <span className="mono">{ean.ean}</span></>}
          </div>
          {ean.etat === 'ok' && ean.produits.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8 }}>
              {ean.produits.map((p) => (
                <CarteProduit key={p.id} produit={p} dansPanier={idsPanier.has(p.id)} onChoisir={onChoisir} />
              ))}
            </div>
          ) : null}
          {ean.etat !== 'loading' && !ean.produits.length ? (
            <div className="soft-card anim-pop" style={{ padding: 16, fontSize: 13, color: 'var(--stone)', lineHeight: 1.5 }}>
              {ean.etat === 'error' ? 'La recherche n’a pas abouti. Réessaie, ou tape le nom du modèle.'
                : ean.trouves ? 'On connaît ce modèle, mais on ne le reprend pas en ce moment.'
                : 'Ce code-barres n’est pas dans notre catalogue. Tape le nom du modèle, ou propose-le dans un lot.'}
              {ean.etat === 'ok' && !ean.trouves ? (
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => go('lot')} style={{ color: 'var(--rouge)', fontWeight: 600, padding: 0 }}>Proposer un lot</button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {enRecherche ? (
        <>
          <div className="t-overline" style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
            {etat === 'loading' ? <Spinner label="Recherche…" />
              : etat === 'ok' ? `${produits.length} résultat${produits.length > 1 ? 's' : ''}`
              : ''}
          </div>

          {etat === 'error' ? (
            <div style={{ padding: '0 16px' }}>
              <div className="soft-card" style={{ padding: 16, fontSize: 13, color: 'var(--stone)' }}>
                Impossible de charger le catalogue ({erreur}). Réessaie dans un instant.
              </div>
            </div>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8, padding: '0 16px' }}>
            {(limite ? produits.slice(0, limite) : produits).map((p) => (
              <CarteProduit key={p.id} produit={p} dansPanier={idsPanier.has(p.id)} onChoisir={onChoisir} />
            ))}
          </div>

          {limite && etat === 'ok' && produits.length > limite ? (
            <div style={{ padding: '10px 16px 0' }}>
              <button className="btn btn-secondary btn-block" onClick={() => go('catalogue', { q: q.trim() })}>
                Voir les {produits.length} résultats <Icons.chev size={16} />
              </button>
            </div>
          ) : null}

          {etat === 'ok' && produits.length === 0 ? (
            <div className="anim-pop" style={{ padding: '4px 16px 0' }}>
              <div className="soft-card" style={{ padding: 18, textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Pas de « {q.trim()} » dans nos références</div>
                {REFERENCEMENT_ACTIF ? (
                  <>
                    <div style={{ fontSize: 13, color: 'var(--stone)', margin: '6px 0 14px', lineHeight: 1.5 }}>
                      On a 2 000+ modèles mais pas celui-là (encore). Demande son référencement : on crée la fiche et on te prévient.
                    </div>
                    <button className="btn btn-primary btn-block" onClick={() => go('referencement', { modele: q.trim() })}>
                      Demander le référencement
                    </button>
                    <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 10, lineHeight: 1.5 }}>
                      Pas de lien vers le produit neuf ?{' '}
                      <button onClick={() => go('lot')} style={{ color: 'var(--rouge)', fontWeight: 600, padding: 0 }}>Propose-le dans un lot</button>
                      , même un seul jouet. Ou fais-en{' '}
                      <button onClick={() => go('don')} style={{ color: 'var(--rouge)', fontWeight: 600, padding: 0 }}>don</button>.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 13, color: 'var(--stone)', margin: '6px 0 14px', lineHeight: 1.5 }}>
                      Vérifie l’orthographe, ou propose-le dans un lot avec tes autres jouets : on l’évalue à la main, même pour un seul jouet.
                    </div>
                    <button className="btn btn-primary btn-block" onClick={() => go('lot')}>
                      Proposer un lot
                    </button>
                    <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 10, lineHeight: 1.5 }}>
                      Tu préfères t’en séparer sans attendre ? Tu peux nous en faire{' '}
                      <button onClick={() => go('don')} style={{ color: 'var(--rouge)', fontWeight: 600, padding: 0 }}>don</button>.
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {/* Page de recherche dédiée : sous les résultats, le repli reste à portée. */}
          {!limite && etat === 'ok' && produits.length > 0 ? (
            <div style={{ padding: '24px 16px 0' }}>
              <div className="t-overline" style={{ marginBottom: 8 }}>Pas trouvé ton modèle ?</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                <button onClick={() => go('lot')} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', textAlign: 'left' }}>
                  <Illus nom="lot" size={40} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>Propose-le dans un lot</div>
                    <div style={{ fontSize: 12, color: 'var(--stone)' }}>Dès 1 jouet, évalué à la main</div>
                  </div>
                  <Icons.chev size={16} style={{ color: 'var(--stone)' }} />
                </button>
                <button onClick={() => go('scan')} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', textAlign: 'left' }}>
                  <Illus nom="photo" size={40} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>L’identifier par photo</div>
                    <div style={{ fontSize: 12, color: 'var(--stone)' }}>Si tu ne connais pas son nom exact</div>
                  </div>
                  <Icons.chev size={16} style={{ color: 'var(--stone)' }} />
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
